import { useEffect, useRef, useState } from "react";
import { Bell, CornerDownRight, Gift, MessageSquare, AtSign } from "lucide-react";
import api from "@/lib/api";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR");
}

export default function NotificationBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell" className="relative p-2 rounded-lg text-[#A1B5A8] hover:text-white hover:bg-[#1A2B20] transition-colors">
        <Bell size={18} />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold grid place-items-center" data-testid="notif-badge">
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-[#00E676]/20 bg-[#131F17] shadow-2xl z-[80]" data-testid="notif-panel">
          <div className="px-4 py-3 border-b border-[#00E676]/15 font-bold text-sm">Notifications</div>
          {data.items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-[#697C6F]">Aucune notification.</p>
          ) : (
            data.items.map((n) => (
              <div key={n.id} className={`px-4 py-3 text-sm border-b border-[#00E676]/5 ${!n.read ? "bg-[#00E676]/5" : ""}`}>
                {n.type === "reaction" ? (
                  <div className="flex items-center gap-2 text-[#39FF14]"><span className="text-base">{n.emoji}</span> <span className="font-semibold">{n.from_name}</span> a réagi à votre message</div>
                ) : n.type === "ban" || n.type === "unban" ? (
                  <div className={n.type === "ban" ? "text-[#FF3B30]" : "text-[#39FF14]"}>
                    <span className="font-semibold">Modération</span>
                    <p className="text-[#C7D3CB] mt-1">{n.text}</p>
                  </div>
                ) : n.type === "referral" ? (
                  <div className="text-[#FFD700]">
                    <span className="font-semibold flex items-center gap-1"><Gift size={14} /> Parrainage</span>
                    <p className="text-[#C7D3CB] mt-1">{n.text}</p>
                  </div>
                ) : n.type === "message" ? (
                  <div className="text-[#39FF14]">
                    <span className="font-semibold flex items-center gap-1"><MessageSquare size={14} /> Réponse de l'administration</span>
                    <p className="text-[#C7D3CB] mt-1 truncate">« {n.text} »</p>
                  </div>
                ) : n.type === "player_message" ? (
                  <div className="text-[#39FF14]">
                    <span className="font-semibold flex items-center gap-1"><MessageSquare size={14} /> {n.from_name}{n.category ? ` — ${n.category}` : ""}</span>
                    <p className="text-[#C7D3CB] mt-1 truncate">« {n.text} »</p>
                  </div>
                ) : n.type === "mention" ? (
                  <div className="text-[#39FF14]">
                    <span className="font-semibold flex items-center gap-1"><AtSign size={14} /> {n.from_name} vous a mentionné</span>
                    <p className="text-[#C7D3CB] mt-1 truncate">« {n.text} »</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-[#39FF14]"><CornerDownRight size={13} /> <span className="font-semibold">{n.from_name}</span> a répondu</div>
                    <p className="text-[#A1B5A8] mt-1 truncate">« {n.text} »</p>
                  </>
                )}
                <span className="text-[10px] text-[#697C6F]">{timeAgo(n.created_at)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
